import React, { useMemo, useState } from "react";
import { Box, MenuItem, Select, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CategoryScale, Chart as ChartJS, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import { Line } from "react-chartjs-2";
import { Alarm } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { InsightsCard } from "./InsightsCard";
import { INSIGHTS_COLORS, TOOLTIP_STYLE, niceScale } from "./colors";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);
const ALL = "all";
/** "HighTemperature" -> "High Temperature" (fallback when there is no translation). */
const humanize = (name) => name.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
/**
 * Card 4: alarms per month for the last 12 months in the browser time zone: acknowledged (solid blue) and
 * unacknowledged (dashed amber), for all alarm types or one (compact selector in the header).
 */
export const MonthlyAlarmsCard = ({ data, loading, error }) => {
    var _a, _b, _c;
    const { t, i18n } = useTranslation(["common"]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [alarmType, setAlarmType] = useState(ALL);
    const typeName = (name) => t(`common:catalog.insights.alarmTypes.${name}`, humanize(name));
    const series = useMemo(() => {
        var _a;
        if (!data)
            return null;
        if (alarmType === ALL)
            return data.allTypes;
        return (_a = data.byType.find((s) => String(s.alarmType) === alarmType)) !== null && _a !== void 0 ? _a : data.allTypes;
    }, [data, alarmType]);
    // Year / month are already in the browser time zone (computed by the API). Axis: "Jan"; tooltip: "January 2026".
    const months = useMemo(() => { var _a; return ((_a = data === null || data === void 0 ? void 0 : data.months) !== null && _a !== void 0 ? _a : []).map((m) => new Date(m.year, m.month - 1, 1)); }, [data === null || data === void 0 ? void 0 : data.months]);
    const labels = months.map((m) => m.toLocaleDateString(i18n.language, { month: "short" }));
    const tooltipTitles = months.map((m) => m.toLocaleDateString(i18n.language, { month: "long", year: "numeric" }));
    const acknowledged = (_a = series === null || series === void 0 ? void 0 : series.acknowledged) !== null && _a !== void 0 ? _a : [];
    const unacknowledged = (_b = series === null || series === void 0 ? void 0 : series.unacknowledged) !== null && _b !== void 0 ? _b : [];
    const total = [...acknowledged, ...unacknowledged].reduce((a, b) => a + b, 0);
    const scale = niceScale([...acknowledged, ...unacknowledged]);
    const acknowledgedLabel = t("common:catalog.insights.acknowledged", "Acknowledged");
    const unacknowledgedLabel = t("common:catalog.insights.unacknowledged", "Unacknowledged");
    const line = {
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.4,
        cubicInterpolationMode: "monotone",
        fill: false,
    };
    const chartData = {
        labels,
        datasets: [
            Object.assign(Object.assign({}, line), { label: acknowledgedLabel, data: acknowledged, borderColor: INSIGHTS_COLORS.acknowledged, backgroundColor: INSIGHTS_COLORS.acknowledged }),
            Object.assign(Object.assign({}, line), { label: unacknowledgedLabel, data: unacknowledged, borderColor: INSIGHTS_COLORS.unacknowledged, backgroundColor: INSIGHTS_COLORS.unacknowledged, borderDash: [6, 5] }),
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: Object.assign(Object.assign({}, TOOLTIP_STYLE), { callbacks: { title: (items) => { var _a, _b, _c; return (_c = tooltipTitles[(_b = (_a = items[0]) === null || _a === void 0 ? void 0 : _a.dataIndex) !== null && _b !== void 0 ? _b : 0]) !== null && _c !== void 0 ? _c : ""; } } }),
        },
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: { color: INSIGHTS_COLORS.tick, font: { size: isMobile ? 10 : 12 }, maxRotation: 0, autoSkip: true },
            },
            y: {
                min: 0,
                max: scale.max,
                border: { display: false },
                grid: { color: INSIGHTS_COLORS.grid },
                ticks: { color: INSIGHTS_COLORS.tick, stepSize: scale.stepSize, padding: 8, font: { size: 12 } },
            },
        },
    };
    const typeSelect = (React.createElement(Select, { size: "small", variant: "standard", disableUnderline: true, value: alarmType, onChange: (e) => setAlarmType(String(e.target.value)), disabled: !data, inputProps: { "aria-label": t("common:catalog.insights.alarmType", "Alarm type") }, 
        // No fixed width: the selected name ("Temperature Reading Delay") is shown in full; only phones cap it.
        sx: {
            fontSize: 13,
            color: "text.secondary",
            flexShrink: 0,
            maxWidth: { xs: 170, sm: "none" },
            mr: 0.5,
            "& .MuiSelect-select": { pr: "36px !important", textOverflow: "ellipsis" },
        } },
        React.createElement(MenuItem, { value: ALL }, t("common:catalog.insights.allTypes", "All types")),
        ((_c = data === null || data === void 0 ? void 0 : data.byType) !== null && _c !== void 0 ? _c : []).map((s) => (React.createElement(MenuItem, { key: s.alarmType, value: String(s.alarmType) }, typeName(s.name))))));
    const legend = [
        { label: acknowledgedLabel, color: INSIGHTS_COLORS.acknowledged },
        { label: unacknowledgedLabel, color: INSIGHTS_COLORS.unacknowledged },
    ];
    return (React.createElement(InsightsCard, { title: t("common:catalog.insights.alarms", "Alarms"), icon: React.createElement(Alarm, { size: 26, color: "currentColor" }), info: t("common:catalog.insights.alarmsInfo", "Alarms per month over the last 12 months (your time zone), by the month the alarm started."), action: typeSelect, loading: loading, error: error, height: 320, emptyText: !loading && !error && total === 0
            ? t("common:catalog.insights.noAlarms", "No alarms in the last 12 months.")
            : null },
        React.createElement(Box, { sx: { display: "flex", flexDirection: "column", height: "100%" } },
            React.createElement(Box, { sx: { position: "relative", flex: 1, minHeight: 0 } },
                React.createElement(Line, { data: chartData, options: options })),
            React.createElement(Box, { sx: { display: "flex", justifyContent: "center", gap: 3, mt: 2 } }, legend.map((item) => (React.createElement(Box, { key: item.label, sx: { display: "flex", alignItems: "center", gap: 1 } },
                React.createElement(Box, { sx: { width: 10, height: 10, borderRadius: "50%", bgcolor: item.color } }),
                React.createElement(Typography, { sx: { fontSize: 12, color: "text.secondary" } }, item.label))))))));
};
